import { FC, useState } from 'react';
import dayjs from 'dayjs';
import {
	Table,
	Form,
	Row,
	Col,
	Select,
	Input,
	Button,
	Divider,
	Space,
	Checkbox,
	DatePicker,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import ANTD_DEFAULT_PROPS from '@/libs/antd/props';
import FleetTitle from '@/libs/antd/components/Title';
import { useApp } from '@/App';
import DataRow from '@/libs/antd/components/DataRow';
import {
	SearchMPPSPFParam,
	useGetMPPCustomerDetail,
	useMPPSPFSearch,
} from '@/libs/fleet-api/services/mpp';
import { useModals } from '@/components/modals/ModalProvider';
import { useForm } from '@/libs/antd/form';
import { formatCurrency } from '@/libs/antd/formater';

const CreateMPPListPage: FC<{ id: string }> = ({ id }) => {
	const { navigate } = useApp();
	const { openCustomerDetail, openCustomerGroupDetail } = useModals();

	const { form, fieldName, value } = useForm<{
		searchType: string;
		searchParam: SearchMPPSPFParam;
	}>();

	const defaultSeachParams = {
		customerId: id,
		pagination: {
			pageNumber: 1,
			pageSize: 10,
		},
	};

	const [searchParam, setSearchParam] =
		useState<SearchMPPSPFParam>(defaultSeachParams);
	const spfSearchQuery = useMPPSPFSearch(searchParam);
	const detailQuery = useGetMPPCustomerDetail({
		id,
	});

	const {
		customerName,
		bmppGroupName,
		customerId,
		bmppGroupId,
		searchByOptions = [],
		financingLimitTypeOptions = [],
		plafonTypeOptions = [],
		orderSourceOptions = [],
		statusOptions = [],
	} = detailQuery.data?.data ?? {};

	const renderSearchType = () => {
		switch (value?.searchType) {
			case 'financing-limit-type':
				return (
					<Form.Item name={fieldName(['searchParam', 'financingLimitTypeId'])}>
						<Select
							options={financingLimitTypeOptions.map((option) => {
								return {
									label: option.value,
									value: option.id,
								};
							})}
							style={{ width: '100%' }}
							allowClear
						/>
					</Form.Item>
				);

			case 'plafon-type':
				return (
					<Form.Item name={fieldName(['searchParam', 'plafonTypeId'])}>
						<Select
							options={plafonTypeOptions.map((option) => {
								return {
									label: option.value,
									value: option.id,
								};
							})}
							style={{ width: '100%' }}
							allowClear
						/>
					</Form.Item>
				);

			case 'order-source':
				return (
					<Form.Item name={fieldName(['searchParam', 'orderSourceId'])}>
						<Select
							options={orderSourceOptions.map((option) => {
								return {
									label: option.value,
									value: option.id,
								};
							})}
							style={{ width: '100%' }}
							allowClear
						/>
					</Form.Item>
				);

			case 'status':
				return (
					<Form.Item name={fieldName(['searchParam', 'statusId'])}>
						<Select
							options={statusOptions.map((option) => {
								return {
									label: option.value,
									value: option.id,
								};
							})}
							style={{ width: '100%' }}
							allowClear
						/>
					</Form.Item>
				);

			case 'approval-date':
				return (
					<Form.Item
						name={fieldName(['searchParam', 'approvalDate'])}
						getValueProps={(value) => ({
							value: value && dayjs(value),
						})}
						normalize={(value) => value && dayjs(value).utc(true).toISOString()}
					>
						<DatePicker format={'DD/MM/YYYY'} />
					</Form.Item>
				);

			case 'expired-date':
				return (
					<Form.Item
						name={fieldName(['searchParam', 'expiredDate'])}
						getValueProps={(value) => ({
							value: value && dayjs(value),
						})}
						normalize={(value) => value && dayjs(value).utc(true).toISOString()}
					>
						<DatePicker format={'DD/MM/YYYY'} />
					</Form.Item>
				);

			default:
				return (
					<Form.Item>
						<Input disabled />
					</Form.Item>
				);
		}
	};

	const [checkedRow, setCheckedRow] = useState<number>();
	const dataSource =
		spfSearchQuery.data?.data?.items.map((item, index) => {
			return {
				...item,
				key: index + 1,
			};
		}) ?? [];

	return (
		<Space
			direction="vertical"
			style={{ display: 'flex' }}
			size={'large'}
		>
			<div
				style={{
					display: 'flex',
					gap: 10,
					marginBottom: 30,
				}}
			>
				<div>
					<button
						style={{
							display: 'flex',
							height: 44,
							width: 44,
							fontSize: 24,
							border: 'none',
							alignItems: 'center',
							justifyContent: 'center',
							background: '#1557BC',
							color: 'white',
							cursor: 'pointer',
						}}
						onClick={() => {
							navigate('MPPListPage');
						}}
					>
						<ArrowLeftOutlined />
					</button>
				</div>
				<div style={{ flex: 1 }}>
					<FleetTitle>Memo Pencairan Pembiayaan (MPP)</FleetTitle>
				</div>
			</div>
			<DataRow
				items={[
					[
						[
							'Nama Customer',
							<a
								href="#"
								onClick={(e) => {
									e.preventDefault();
									if (!customerId) return;
									openCustomerDetail(customerId);
								}}
							>
								{customerName}
							</a>,
						],
						[
							'Group Customer BMPP',
							<a
								href="#"
								onClick={(e) => {
									e.preventDefault();
									if (!bmppGroupId) return;
									openCustomerGroupDetail(bmppGroupId);
								}}
							>
								{bmppGroupName}
							</a>,
						],
					],
				]}
			/>
			<Form
				{...ANTD_DEFAULT_PROPS.FORM}
				form={form}
				layout="horizontal"
				colon={false}
				labelAlign="left"
				onFinish={(values) => {
					if (values.searchType) {
						setSearchParam((searchParam) => {
							return {
								...searchParam,
								...values.searchParam,
								pagination: {
									pageNumber: 1,
									pageSize: 10,
								},
							};
						});
					} else {
						setSearchParam(defaultSeachParams);
					}
				}}
			>
				<Row gutter={[20, 20]}>
					<Col flex="none">
						<Form.Item
							name={fieldName('searchType')}
							label={<span>Kata Kunci</span>}
							labelCol={{ flex: 'auto', style: { width: 150 } }}
							wrapperCol={{ flex: 'none', style: { width: 250 } }}
						>
							<Select
								loading={detailQuery.isLoading}
								disabled={spfSearchQuery.isFetching}
								placeholder="Search By"
								options={searchByOptions
									.map((option) => {
										if (option.value === 'Plafon Type') {
											option.value = 'Jenis Plafon';
										}
										return option;
									})
									.map((option) => {
										return {
											label: option.value,
											value: option.id,
										};
									})}
								allowClear
								style={{
									display: 'block',
									width: '100%',
								}}
							/>
						</Form.Item>
					</Col>
					<Col flex="auto">{renderSearchType()}</Col>
					<Col>
						<Form.Item>
							<Button
								block
								disabled={detailQuery.isLoading || spfSearchQuery.isFetching}
								type="primary"
								htmlType="submit"
							>
								SEARCH
							</Button>
						</Form.Item>
					</Col>
				</Row>
			</Form>

			<Divider style={{ margin: 0 }} />
			<div>
				<strong>Informasi Limit Pembiayaan</strong>
			</div>

			<Table
				loading={spfSearchQuery.isFetching}
				bordered
				scroll={{ x: 'max-content' }}
				dataSource={dataSource}
				columns={[
					{
						title: 'No',
						align: 'center',
						fixed: 'left',
						render: (_, __, index) => index + 1,
					},
					{
						title: 'No.MKL',
						render: (_, record) => record.mklNo || '-',
					},
					{
						title: 'No.SPF',
						render: (_, record) =>
							record.spfNo && record.napResolutionId ? (
								<a
									href="#"
									onClick={(e) => {
										e.preventDefault();
										// TODO: change to actual prospect id
										navigate('SPFPage', {
											prospectId: '',
											spfId: record.spfId,
											napResolutionId: record.napResolutionId,
											viewOnly: true,
											onBack: () => {
												navigate('CreateMPPListPage', {
													id,
												});
											},
										});
									}}
								>
									{record.spfNo}
								</a>
							) : (
								'-'
							),
					},
					{
						title: 'Tanggal Persetujuan',
						render: (_, record) =>
							record.approvalDate
								? dayjs(record.approvalDate).format('DD/MM/YYYY')
								: '-',
					},
					{
						title: 'Limit Pembiayaan Existing',
						render: (_, record) =>
							record.existingFinancingLimit
								? formatCurrency(record.existingFinancingLimit)
								: '-',
					},
					{
						title: 'Sifat Limit',
						render: (_, record) => record.financingLimitType || '-',
					},
					{
						title: 'Jenis Plafon',
						render: (_, record) => record.plafonType || '-',
					},
					{
						title: 'Expired Date',
						render: (_, record) =>
							record.expiredDate
								? dayjs(record.expiredDate).format('DD/MM/YYYY')
								: '-',
					},
					{
						title: 'NTF',
						render: (_, record) =>
							record.ntfAmount ? formatCurrency(record.ntfAmount) : '-',
					},
					{
						title: 'OS Principal',
						render: () => '-',
					},
					{
						title: 'Status MKL',
						render: (_, record) => record.mklStatus || '-',
					},
					{
						title: 'Select MKL',
						align: 'center',
						fixed: 'right',
						render: (_, __, index) => (
							<Checkbox
								checked={checkedRow === index}
								onChange={(e) => {
									setCheckedRow(e.target.checked ? index : undefined);
								}}
							/>
						),
					},
				]}
				pagination={{
					total: spfSearchQuery.data?.data.totalCount,
					onChange: (pageNumber) => {
						setSearchParam((prev) => ({
							...prev,
							pagination: {
								...prev.pagination,
								pageNumber,
							},
						}));
					},
				}}
			/>

			<Button
				type="primary"
				htmlType="submit"
				style={{
					display: 'block',
					width: 200,
					marginLeft: 'auto',
				}}
				disabled={checkedRow === undefined}
				onClick={() => {
					const selected =
						typeof checkedRow === 'number' ? dataSource[checkedRow] : undefined;

					if (selected === undefined) return;
					navigate('MPPPage', {
						spfId: selected.spfId,
					});
				}}
			>
				CREATE MPP
			</Button>
		</Space>
	);
};

export default CreateMPPListPage;
